import React from "react";
import PropTypes from "prop-types";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import AdHocNewsRecipentForm from "./AdHocNewsRecipentForm";
import { FormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import TablePageReport from "../../../../components/TablePageReport";
import { TableEmptyMessage } from "../../../../components/Utils";

const getState = (model) => {
  const items = model || [];
  return {
    items: items.map((item, index) => ({ ...item, index })),
    error: null,
    first: 0,
  };
};

function AdHocNewsRecipentListForm({
  forwardedRef,
  className = "",
  model = [],
  rows = 10,
  namePrefix = "",
  iseditable,
  disabled: _disabled,
  readOnly,
  onChange,
}) {
  const formRef = React.useRef(null);
  const [state, setState] = React.useState(getState(model));

  const validate = React.useCallback(() => {
    const { items } = state;
    return [...items];
  }, [state]);

  const getModel = React.useCallback(
    (noValidate) => validate(noValidate),
    [validate],
  );
  React.useImperativeHandle(forwardedRef, () => ({ getModel }));

  const onSave = React.useCallback(
    (onHide) => {
      const model = formRef.current.getModel();
      console.log(">> saving recipient ...", model);
      if (!model) return;
      const items = state.items;
      if (model.index !== undefined && items[model.index] !== undefined) {
        items[model.index] = model;
      } else {
        model.index = items.length;
        items.push(model);
      }
      setState((prev) => ({ ...prev, items }));
      onChange && onChange(items);
      onHide && onHide();
    },
    [state, formRef.current, onChange],
  );

  const onDelete = React.useCallback(
    (selected) => {
      const items = [];
      state.items.forEach((item) => {
        if (item.index !== selected.index) {
          items.push(item);
        }
      });
      setState((prev) => ({ ...prev, items }));
      onChange && onChange(items);
    },
    [state, onChange],
  );

  const isDisabled = React.useMemo(
    () => readOnly || _disabled || iseditable !== "true",
    [readOnly, _disabled, iseditable],
  );

  const onPage = React.useCallback(
    (event) => setState((prev) => ({ ...prev, first: event.first })),
    [],
  );

  const renderItemTable = React.useCallback(
    (items, actionTemplate) => {
      return (
        <DataTable
          value={items}
          rows={rows}
          paginator={items.length > rows}
          first={state.first}
          onPage={onPage}
          paginatorLeft={
            <TablePageReport
              className="pad-lft"
              totalRecords={items.length}
              filteredRecords={items.length}
              first={state.first}
              rows={rows}
              emptyText="Keine Einträge"
              itemName="Eintrag"
              itemNamePlural="Einträge"
            />
          }
          emptyMessage={<TableEmptyMessage itemNamePlural="Einträge" min />}
        >
          <Column
            field="name"
            header="Name"
            sortable={items.length > 1}
            filter={items.length > 1}
            filterMatchMode="contains"
          />
          <Column
            field="email"
            header="E-Mail"
            sortable={items.length > 1}
            filter={items.length > 1}
            filterMatchMode="contains"
          />
          {!isDisabled && (
            <Column
              body={actionTemplate}
              style={{ textAlign: "center", width: "7.4em" }}
            />
          )}
        </DataTable>
      );
    },
    [state, rows, state, onPage, isDisabled],
  );

  const renderItemForm = React.useCallback(
    (item, onHide) => {
      const model = item || {
        name: "",
        email: "",
      };
      return (
        <div>
          <AdHocNewsRecipentForm
            ref={formRef}
            key={Math.random()}
            model={model}
            disabled={isDisabled}
            namePrefix={namePrefix}
          />
          <FormMenu
            className="margin-big-top w3-border-top pad-big w3-light-grey"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            onSave={() => onSave(onHide)}
            onCancel={onHide}
            saveBtnName="saveRecipientBtn"
            cancelBtnName="cancelRecipientBtn"
            saveHidden={isDisabled}
            cancelBtnLabel={isDisabled ? "Schließen" : undefined}
          />
        </div>
      );
    },
    [isDisabled, onSave],
  );

  const getItemFormTitle = React.useCallback((model) => {
    let title = "hinzufügen";
    if (model) {
      title = isDisabled ? "anzeigen" : "bearbeiten";
    }
    return <h3 className="no-margin">{`News Verteilereintrag ${title}`}</h3>;
  }, []);

  const titleClassName = `no-margin-top`;
  return (
    <ItemListManager
      key={Math.random()}
      className={className}
      title={<h3 className={titleClassName}>{"Ad-hoc News Verteiler"}</h3>}
      addBtnLabel="Hinzufügen"
      addBtnName={`${namePrefix}addRecipientBtn`}
      addBtnDisabled={isDisabled}
      addBtnHidden={isDisabled}
      items={state.items}
      getItemFormTitle={getItemFormTitle}
      renderItemTable={renderItemTable}
      renderItemForm={renderItemForm}
      namePrefix="region"
      itemDeleteable={!isDisabled}
      deleteTitle="News Verteilereintrag löschen"
      getDeleteText={(item) =>
        `Wollen Sie den Eintrag (${item.name}, ${item.email}) unwiderruflich löschen?`
      }
      onDelete={onDelete}
    />
  );
}

AdHocNewsRecipentListForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
  ),
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  rows: PropTypes.number,
  namePrefix: PropTypes.string,
  iseditable: PropTypes.string,
  onChange: PropTypes.func,
};

export default React.forwardRef((props, ref) => (
  <AdHocNewsRecipentListForm {...props} forwardedRef={ref} />
));
